/**
 * V70 엔글 마블 — 이벤트 수치 설정 (Phase 2 초기값)
 *
 * 사용자 지시: Phase 2 는 QA 테스트용 초기값, Phase 3 에서 조정 가능.
 * 모든 EV / 확률 / 보상은 이 모듈에 집중 → 1곳만 수정하면 전체 영향.
 *
 * 시뮬 결과 근거 (420,000 rolls × 10 반복):
 *   - 평균 2,868점, σ 830, TOP3 완전 동점 0%
 *   - 잭팟 제거 시 TOP3 변동 13.3% (잭팟이 상위권에 유의미한 영향)
 *   - 안전형 TOP3 진입률 0%, challenge 60~80% 쏠림 (조정 안건은 Phase 3 재검토)
 */

export type EventKind =
  | 'safe' | 'E1' | 'E2' | 'E3' | 'E4' | 'E5'
  | 'E6' | 'E7' | 'E8' | 'E9' | 'E10'
  // V2 신규 — pending 이벤트 전용 (보드 셔플 pool 에는 없음)
  | 'vault' | 'shop';

export interface WeightedReward { value: number; prob: number; label?: string }

/**
 * E1 — 확정 vs 랜덤 (실질 분산 분기, EV 동일)
 *   A: 확정 +100   σ=0
 *   B: [+300, +100, -100] 1/3 각   EV +100, σ=163
 */
export const E1_CONFIG = {
  options: [
    { id: 'A', label: '확정 보상', rewards: [{ value: +100, prob: 1 }] as WeightedReward[] },
    { id: 'B', label: '랜덤 보상', rewards: [
      { value: +300, prob: 1/3, label: '대박' },
      { value: +100, prob: 1/3, label: '보통' },
      { value: -100, prob: 1/3, label: '실패' },
    ] as WeightedReward[] },
  ],
} as const;

/**
 * E2 — 가위바위보 (서버 1/3 균등, 선택은 UI 전시)
 *   승 +200 / 비김 +50 / 패 -100   EV +50
 */
export const E2_CONFIG = {
  outcomes: [
    { value: +200, prob: 1/3, label: '승' },
    { value: +50,  prob: 1/3, label: '비김' },
    { value: -100, prob: 1/3, label: '패' },
  ] as WeightedReward[],
} as const;

/**
 * E3 — 보물상자 (EV + σ 분기, 실질 위험·보상)
 *   A: +150 확정                           EV +150, σ=0
 *   B: +250(60%) / +50(40%)                EV +170, σ=98
 *   C: +400(30%) / 0(40%) / -100(30%)      EV +90, σ=215
 */
export const E3_CONFIG = {
  options: [
    { id: 'A', label: '안전 상자', rewards: [{ value: +150, prob: 1 }] as WeightedReward[] },
    { id: 'B', label: '중위험 상자', rewards: [
      { value: +250, prob: 0.6 },
      { value: +50,  prob: 0.4 },
    ] as WeightedReward[] },
    { id: 'C', label: '고위험 상자', rewards: [
      { value: +400, prob: 0.3 },
      { value: 0,    prob: 0.4 },
      { value: -100, prob: 0.3 },
    ] as WeightedReward[] },
  ],
} as const;

/** E4 — 동전 (앞/뒤 pick, 서버 50:50) */
export const E4_CONFIG = {
  outcomes: [
    { value: +200, prob: 0.5, label: '적중' },
    { value: -100, prob: 0.5, label: '미적중' },
  ] as WeightedReward[],
} as const;

/**
 * E5 — 폭탄 (3선 pick, EV + σ 분기)
 *   선 1: +100 확정             EV +100, σ=0
 *   선 2: +300(70%) / -200(30%) EV +150, σ=229
 *   선 3: +500(40%) / -400(60%) EV -40, σ=441 (합리적 플레이어는 선택 X)
 */
export const E5_CONFIG = {
  options: [
    { id: 1, label: '안전선', rewards: [{ value: +100, prob: 1 }] as WeightedReward[] },
    { id: 2, label: '중위험선', rewards: [
      { value: +300, prob: 0.7 },
      { value: -200, prob: 0.3 },
    ] as WeightedReward[] },
    { id: 3, label: '극위험선', rewards: [
      { value: +500, prob: 0.4 },
      { value: -400, prob: 0.6 },
    ] as WeightedReward[] },
  ],
} as const;

/** E6 — 운명의 카드 (5장 shuffle, 사용자 1장 pick, EV +30, σ=112) */
export const E6_CONFIG = {
  deck: [+200, +100, 0, -50, -100] as const,
} as const;

/** E7 — 홀짝 (사용자 pick, 서버 50:50) */
export const E7_CONFIG = {
  outcomes: [
    { value: +250, prob: 0.5, label: '적중' },
    { value: -150, prob: 0.5, label: '미적중' },
  ] as WeightedReward[],
} as const;

/** E8 — 방패 획득 (최대 3개, 손실 이벤트 발생 시 자동 사용) */
export const E8_CONFIG = {
  maxShields: 3,
} as const;

/** E9 — 워프 (3곳 pick, E9 자체 제외, 체인 깊이 1로 제한) */
export const E9_CONFIG = {
  optionCount: 3,
  maxChainDepth: 1,
} as const;

/** E10 — 잭팟 (즉시, EV +51) */
export const E10_CONFIG = {
  dist: [
    { value: +600, prob: 0.01, label: '🎊 메가 잭팟' },
    { value: +200, prob: 0.10, label: '중박' },
    { value: +50,  prob: 0.50, label: '소박' },
    { value: 0,    prob: 0.39, label: '꽝' },
  ] as WeightedReward[],
} as const;

/** 보드 전역 정책 */
export const MARBLE_GLOBAL_CONFIG = {
  boardSize: 24,
  safePositions: [0, 6, 12, 18] as const,
  diceDailyLimit: 3,
  diceSides: 6,
  gracePeriodMinutes: 60,
  leaderboardCacheTtlSec: 60,
  tiebreakerWindowHours: 48,
} as const;

/** V2 특수 모서리 역할 (index 기반) */
export const V2_SAFE_ROLES = {
  START: 0,
  TRAP: 6,
  VAULT: 12,
  SHOP: 18,
} as const;

/** V2 — START 완주 보상 */
export const LAP_REWARD = 300;

/** V2 — 무인도 규칙 */
export const TRAP_CONFIG = {
  successThreshold: 4,       // dice >= 4 성공
  guaranteeAfterFails: 2,    // trapFails >= 2 → 3차 보장
} as const;

/** V2 — 보너스 금고 */
export const VAULT_CONFIG = {
  options: [
    { id: 'A', label: '안전한 금고', rewards: [{ value: +100, prob: 1 }] as WeightedReward[] },
    { id: 'B', label: '행운의 금고', rewards: [
      { value: +400, prob: 0.10, label: '🎊 대박' },
      { value: +250, prob: 0.20, label: '좋음' },
      { value: +100, prob: 0.40, label: '보통' },
      { value: +50,  prob: 0.30, label: '소박' },
    ] as WeightedReward[] },
  ],
} as const;

/** V2 — 아이템 상점 */
export const SHOP_CONFIG = {
  shieldPrice: 150,
  speedPrice: 100,
  shieldMax: 3,
  speedMax: 1,
} as const;

/** V2 — 가속 아이템 */
export const SPEED_CONFIG = {
  bonus: 2,            // +2 칸
  maxMoveDistance: 8,  // 상한
} as const;
